import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Box,
  Delete,
  Edit,
  Eye,
  Plus,
  Search,
  Trash,
  ArrowUpDown,
  ChevronDown,
  Filter,
} from "lucide-react";
import ConfirmDeleteModal from "../../Modals/ConfirmDeleteModal";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AllClientsThunk,
  DeleteClientThunk,
} from "../../redux/states/clients/thunk";

const ManageClients = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clients, loading, error } = useSelector((state) => state.clients);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredClients, setFilteredClients] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  // ✅ Fetch all clients on mount
  useEffect(() => {
    dispatch(AllClientsThunk());
  }, [dispatch]);

  // ✅ Filter and sort clients
  useEffect(() => {
    if (clients && clients.length > 0) {
      let filtered = clients.filter(
        (client) =>
          client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.slug?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Sorting
      filtered = filtered.sort((a, b) => {
        const aValue = a[sortField] || "";
        const bValue = b[sortField] || "";

        if (sortDirection === "asc") {
          return aValue.toString().localeCompare(bValue.toString());
        } else {
          return bValue.toString().localeCompare(aValue.toString());
        }
      });

      setFilteredClients(filtered);
    } else {
      setFilteredClients([]);
    }
  }, [clients, searchTerm, sortField, sortDirection]);

  // ✅ Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // ✅ Delete handlers
  const handleDeleteClick = (uuid) => {
    setSelectedId(uuid);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedId) {
      dispatch(DeleteClientThunk(selectedId))
        .unwrap()
        .then(() => {
          toast.success("Client deleted successfully!");
        })
        .catch((err) => {
          toast.error(err?.message || "Failed to delete client");
        });
      setModalOpen(false);
      setSelectedId(null);
    }
  };

  // ✅ Navigation
  const handleEdit = (uuid) => navigate(`/update-new-clients/${uuid}`);
  const handleView = (uuid) => navigate(`/view-clients/${uuid}`);
  const handleCreate = () => {
    navigate("/add-new-clients");
  };

  // ✅ Sort icon component
  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return (
      <ChevronDown
        className={`w-4 h-4 ml-1 transition-transform ${
          sortDirection === "desc" ? "rotate-180" : ""
        }`}
      />
    );
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen  p-2">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                Clients
              </h1>
              <p className="text-gray-600 mt-2">Total Clients : {filteredClients.length}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCreate}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Plus className="w-4 h-4 mr-2" /> New Clients
              </button>
            </div>
          </div>
        </div>

        {/* Search & Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <div className="lg:col-span-3 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"></div>
            <input
              type="text"
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm shadow-sm transition-all duration-300"
              placeholder="Search by category name or slug..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl shadow-sm">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Loading categories...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-center">
              <div className="bg-red-100 p-2 rounded-lg mr-4">
                <Trash className="text-red-600 w-5 h-5" />
              </div>
              <div>
                <h3 className="text-red-800 font-semibold">
                  Error Loading Categories
                </h3>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Clients Table */}
        {!loading && filteredClients.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center">
                        Category Name
                        <SortIcon field="name" />
                      </div>
                    </th>
                  
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Created Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredClients?.map((client, index) => (
                    <tr
                      key={client.uuid || index}
                      className="hover:bg-gray-50 transition-colors duration-150 group"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm mr-3">
                            {client?.name?.charAt(0)?.toUpperCase() || "C"}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                              {client?.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              City: {client?.profile_data.city || "N/A"}
                            </div>
                          </div>
                        </div>
                      </td>
                    
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            client.status === "active"
                              ? "bg-green-100 text-green-800 border border-green-200"
                              : "bg-red-100 text-red-800 border border-red-200"
                          }`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full mr-2 ${
                              client.status === "active"
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                          ></div>
                          {client.status || "inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {client.created_at
                          ? new Date(client.created_at).toLocaleDateString(
                              "en-IN"
                            )
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleView(client.uuid)}
                            className="inline-flex items-center px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium transition-colors duration-200 group/btn"
                            title="View Category"
                          >
                            <Eye className="w-4 h-4 mr-1 group-hover/btn:scale-110 transition-transform" />
                          </button>
                          <button
                            onClick={() => handleEdit(client.uuid)}
                            className="inline-flex items-center px-3 py-2 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 rounded-lg text-xs font-medium transition-colors duration-200 group/btn"
                            title="Edit Category"
                          >
                            <Edit className="w-4 h-4 mr-1 group-hover/btn:scale-110 transition-transform" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(client.uuid)}
                            className="inline-flex items-center px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-xs font-medium transition-colors duration-200 group/btn"
                            title="Delete Category"
                          >
                            <Delete className="w-4 h-4 mr-1 group-hover/btn:scale-110 transition-transform" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredClients.length === 0 && clients.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl shadow-sm">
            <div className="bg-gray-100 p-8 rounded-2xl mb-6">
              <Box className="w-16 h-16 text-gray-400 mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-600 mb-2">
              No Categories Found
            </h3>
            <p className="text-gray-500 mb-6 max-w-md">
              Get started by creating your first category to organize your
              content effectively.
            </p>
            <button
              onClick={handleCreate}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Your First Category
            </button>
          </div>
        )}

        {/* No Search Results */}
        {!loading && filteredClients.length === 0 && clients.length > 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl shadow-sm">
            <div className="bg-gray-100 p-8 rounded-2xl mb-6">
              <Search className="w-16 h-16 text-gray-400 mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-600 mb-2">
              No Matching Categories
            </h3>
            <p className="text-gray-500 mb-6">
              No categories found matching "
              <span className="font-semibold">{searchTerm}</span>"
            </p>
            <button
              onClick={() => setSearchTerm("")}
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center transition-all duration-300"
            >
              Clear Search
            </button>
          </div>
        )}

        {/* Delete Modal */}
        <ConfirmDeleteModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Category"
          message="Do you really want to delete this category? This action cannot be undone and all associated data will be permanently removed."
        />
      </div>
    </NonAuthLayout>
  );
};

export default ManageClients;
